/**
 * OpenList 文件预览相关 Hook
 */

import { useState, useCallback } from "react"
import { getFilePreview, getFileDownload } from "@/lib/actions/file-actions"
import type { OpenListFile } from "@/types/openlist"
import { convertFileTypeForPreview, formatFileSize } from "@/lib/utils/openlist-file-utils"
import { downloadFile } from "@/utils"

interface PreviewFile {
  id: string
  name: string
  type: string
  url: string
  size?: string
  isLoading?: boolean
}

interface UseOpenListPreviewProps {
  subjectId: string
}

export function useOpenListPreview({ subjectId }: UseOpenListPreviewProps) {
  const [clickedFileId, setClickedFileId] = useState<string | null>(null)
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null)
  const [isDownloading, setIsDownloading] = useState<Set<string>>(new Set())

  // 文件预览
  const handlePreview = useCallback(async (file: OpenListFile) => {
    // 设置点击状态，提供即时反馈
    setClickedFileId(file.path)

    // 立即打开弹窗，显示加载状态
    setPreviewFile({
      id: file.path,
      name: file.name,
      type: convertFileTypeForPreview(file.fileType, file.name),
      url: "", // 暂时为空，显示加载状态
      size: formatFileSize(file.size),
      isLoading: true,
    })

    // 后台获取预览URL
    try {
      const result = await getFilePreview(subjectId, file.path)
      if (result.success && result.previewUrl) {
        // 更新弹窗内容
        setPreviewFile(prev =>
          prev
            ? {
                ...prev,
                url: result.previewUrl || "",
                isLoading: false,
              }
            : null
        )
        setClickedFileId(null)
      } else {
        console.error("获取预览URL失败:", result.error)
        // 显示错误状态
        setPreviewFile(prev =>
          prev
            ? {
                ...prev,
                url: "",
                isLoading: false,
              }
            : null
        )
        setClickedFileId(null)
      }
    } catch (error) {
      console.error("获取预览URL失败:", error)
      // 显示错误状态
      setPreviewFile(prev =>
        prev
          ? {
              ...prev,
              url: "",
              isLoading: false,
            }
          : null
      )
      setClickedFileId(null)
    }
  }, [subjectId])

  // 文件下载
  const handleDownload = useCallback(async (file: OpenListFile) => {
    setIsDownloading(prev => new Set(prev).add(file.path))
    
    try {
      const result = await getFileDownload(subjectId, file.path)
      if (result.success && result.downloadUrl) {
        downloadFile(result.downloadUrl)
      } else {
        console.error("获取下载链接失败:", result.error)
      }
    } catch (err) {
      console.error("下载失败:", err)
    } finally {
      setIsDownloading(prev => {
        const newSet = new Set(prev)
        newSet.delete(file.path)
        return newSet
      })
    }
  }, [subjectId])

  // 关闭预览
  const closePreview = useCallback(() => {
    setPreviewFile(null)
    setClickedFileId(null)
  }, [])

  return {
    clickedFileId,
    previewFile,
    isDownloading,
    handlePreview,
    handleDownload,
    closePreview
  }
}
